/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/no-unused-vars */
import rowStyles from '@/components/StoreProductsTable/TableRow.module.css';
import { Modifier } from '@/types/menus.types';
import { Box, Grid } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FZMenuModifierTable from './FZMenuModifierTable';
import styles from './FZStoreModifierItemTable.module.css';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { FZStoreModifierItemTableProps } from '@/components/ModifiersTable/ModifiersTable.types';
import { t } from 'i18next';

const CellModifierDescription = ({
  row,
  modifierCategory,
}: CellContext<Modifier, unknown> & { modifierCategory: string }) => (
  <Grid container direction="column" justifyContent="center" alignItems="flex-start" width="0px">
    <Grid item className={styles.description}>
      {row.original.modifierName}
    </Grid>
    <Grid item className={styles.subDescription}>
      <span className={styles.productDescription}>{row.original.modifierDescription}</span>
    </Grid>
  </Grid>
);

function getFirstEightChars(header: string): string {
  return header.substring(0, 8);
}

// Update getPriceDescription to use the modifierCategory (passed via props) and form proper modifierKeys object.
const getPriceDescription =
  (header: string, setPriceDeviationsData: any, modifierCategory: string) =>
  ({ row }: CellContext<Modifier, unknown>) => {
    const { variantId, slotId, modifierId, modifierWeightId, storeGroups } = row.original;
    const { storeGroupName, chosenPriceRange } = storeGroups.find((sg) => sg.storeGroupName === header) || {};
    if (!chosenPriceRange) return '--';
    const formattedPriceRange = `$${chosenPriceRange}`.replace(/-/g, ' - ');
    return formattedPriceRange.includes('-') ? (
      <div className={styles.rangePriceCell}>
        <div>{formattedPriceRange}</div>
        <InfoOutlined
          className={styles.priceRangeInfo}
          sx={{ width: '22px', height: '22px', color: '#266DCC' }}
          onClick={() =>
            setPriceDeviationsData({
              selectedGroupName: storeGroupName,
              modifierCategory, // dynamic modifierCategory from props
              modifierKeys: [
                {
                  modifierId: row.original.modifierId,
                  slotId: row.original.slotId,
                  modifierWeightId: row.original.modifierWeightId,
                  variantId: row.original.variantId,
                },
              ],
              isPriceDeviationsDrawerOpen: true,
            })
          }
        />
      </div>
    ) : (
      formattedPriceRange
    );
  };

const FZStoreModifierItemTable = (props: FZStoreModifierItemTableProps) => {
  const {
    changeHeaderSize,
    menuModifiers,
    dynamicHeaders,
    onHandleSetVariantId, // will be replaced by onHandleSetKeys in upstream components
    modifierCategory, // new prop passed from FZStoreMenuModifiersDetails.tsx
    variant,
    setPriceDeviationsData,
    selectedRows,
    onRowSelectionChange,
    isLoading,
    storesAndGroups,
    isFranchiseeEditAndFreeze,
    onShowLockedModal,
    lockedModifiers = [],
  } = props;

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;
      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count as number;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length as number;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount as number;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length as number;
      }

      const headerWithFistEightChars = getFirstEightChars(header);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <strong>{headerWithFistEightChars}</strong>
          <span>
            {count} / {total}
          </span>
        </div>
      );
    },
    [storesAndGroups],
  );

  const columns = useMemo(() => {
    const lColumns = [
      {
        header: t('menuDetails.storeItem.table.modifier'),
        accessorKey: 'modifierDescription',
        enableSorting: true,
        cell: (cellProps: CellContext<Modifier, unknown>) =>
          CellModifierDescription({ ...cellProps, modifierCategory }),
        sortingFn: 'sortModifier',
        columnClassname: 'productHeaderStyle',
      } as any,
    ];
    dynamicHeaders.forEach((header, index) => {
      lColumns.push({
        header: getStoreGroupsCount(header),
        accessorKey: header,
        columnClassname:
          index < dynamicHeaders.length - 1
            ? dynamicHeaders.length < 6
              ? 'dynamicHeader150'
              : 'dynamicHeader'
            : 'dynamicHeaderLastItem',
        enableSorting: true,
        cell: getPriceDescription(header, setPriceDeviationsData, modifierCategory),
        sortingFn: 'sortModifierByPrice',
      } as any);
    });

    return lColumns;
  }, [t, dynamicHeaders, modifierCategory]);

  return (
    <div className={rowStyles.tablePage}>
      <div>
        <Box>
          <FZMenuModifierTable
            returnedRecordsText={(count) => t('menu.table.returnedRecord', { count })}
            data={menuModifiers}
            isLoading={isLoading}
            enableRowSelection
            columns={columns}
            onHandleSetVariantId={onHandleSetVariantId}
            variant={variant}
            selectedRows={selectedRows}
            onRowSelectionChange={onRowSelectionChange}
            changeHeaderSize={changeHeaderSize}
            isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
            onShowLockedModal={onShowLockedModal}
            lockedItems={lockedModifiers}
          />
        </Box>
      </div>
    </div>
  );
};

export default FZStoreModifierItemTable;
